// Durable read-cursor state. This module is the ONLY writer of channel read
// position: local `channel_read_markers` rows and the server `mark_as_read`
// endpoint both funnel through here. Session-visual state (badges, overlays,
// dividers) lives in `unread_store.js`; the policy for when to advance lives in
// `channelReadLifecycle.js` and the feed's rendered-tail controller
// (feed/echo/readCursor.ts).
import Dexie from 'dexie';

import { apiPost } from './api_v2';
import db from './db';
import { logDebug } from './logger';
import { sortULIDs } from './stringUtils';

export function mergeMonotonicMarker(existingRow, incomingRow) {
  if (!existingRow || !incomingRow) return incomingRow;
  if (
    existingRow.last_read_sort_order &&
    incomingRow.last_read_sort_order &&
    sortULIDs(existingRow.last_read_sort_order, incomingRow.last_read_sort_order) > 0
  ) {
    return { ...incomingRow, last_read_sort_order: existingRow.last_read_sort_order };
  }

  return incomingRow;
}

const channelReadMarkerKey = (marker) => [marker.workspace_id, marker.id];

export async function putMergedChannelReadMarker(marker) {
  if (!marker) return null;

  return db.transaction('rw', db.channel_read_markers, async () => {
    const existingMarker = await db.channel_read_markers.get(channelReadMarkerKey(marker));
    const mergedMarker = mergeMonotonicMarker(existingMarker, marker);
    await db.channel_read_markers.put(mergedMarker);
    return mergedMarker;
  });
}

export async function bulkPutMergedChannelReadMarkers(readMarkers) {
  if (!readMarkers?.length) return [];

  return db.transaction('rw', db.channel_read_markers, async () => {
    const existingMarkers = await db.channel_read_markers.bulkGet(readMarkers.map(channelReadMarkerKey));
    const mergedReadMarkers = readMarkers.map((marker, index) => mergeMonotonicMarker(existingMarkers[index], marker));
    await db.channel_read_markers.bulkPut(mergedReadMarkers);
    return mergedReadMarkers;
  });
}

export async function advanceReadCursor(
  workspaceId,
  channelId,
  profileId,
  via,
  { sortOrder = null, keepalive = false, forceNetwork = false } = {},
) {
  if (!workspaceId || !channelId || !profileId) return;

  let target = sortOrder;
  if (!target) {
    const latest = await db.messages
      .where('[channel_id+sort_order]')
      .between([channelId, Dexie.minKey], [channelId, Dexie.maxKey])
      .reverse()
      .filter((message) => message.pseudotype !== 'optimistic')
      .first();
    target = latest?.sort_order;
  }
  if (!target) return;

  const rows = await db.channel_read_markers.where('[workspace_id+channel_id]').equals([workspaceId, channelId]).toArray();
  const marker = rows.find((row) => String(row.profile_id) === String(profileId)) || null;
  if (!marker && rows.length > 0) {
    logDebug('unreads', 'advanceReadCursor skipped local write because profile marker was not found', {
      workspaceId,
      channelId,
      profileId,
      markerProfileIds: rows.map((row) => row.profile_id),
    });
  }

  const alreadyReadLocally = marker?.last_read_sort_order && sortULIDs(target, marker.last_read_sort_order) <= 0;
  if (alreadyReadLocally && !forceNetwork) return;

  if (marker && !alreadyReadLocally) {
    await db.channel_read_markers.update([marker.workspace_id, marker.id], { last_read_sort_order: target });
  }

  logDebug('unreads', `advanceReadCursor via ${via}`, channelId, target);
  void apiPost(`/channels/${channelId}/mark_as_read?via=${via}`, { sort_order: target }, keepalive ? { keepalive: true } : {});
}

// Marks the whole channel read server-side with a bare keepalive POST (the server
// clamps to its latest message). Teardown-only: pagehide/unload can't wait for the
// async Dexie reads advanceReadCursor needs, so this fire-and-forget path exists to
// get *something* durable out before the page dies. Everywhere else, use
// advanceReadCursor so the cursor only covers messages the client has actually seen.
export function markChannelFullyRead(channelId, via) {
  if (!channelId) return;

  apiPost(`/channels/${channelId}/mark_as_read?via=${via}`, {}, { keepalive: true });
  logDebug('unreads', `markChannelFullyRead keepalive request sent via ${via}`, channelId);
}
